import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { LevelDetail, OrdinalLevelDef } from '@/lib/rubric-publish-payload'
import { STANDARD_ORDINAL_LEVELS } from '@/lib/rubric-publish-payload'

type LevelsGridEditorProps = {
  levels: OrdinalLevelDef[]
  levelDetails: Record<string, LevelDetail>
  onChangeLevels: (levels: OrdinalLevelDef[]) => void
  onChangeDetails: (details: Record<string, LevelDetail>) => void
  /** When true, toolbar is rendered by parent (CompetencyLadderEditor). */
  hideToolbar?: boolean
}

export function LevelsGridEditor({
  levels,
  levelDetails,
  onChangeLevels,
  onChangeDetails,
  hideToolbar = false,
}: LevelsGridEditorProps) {
  function updateLevel(index: number, patch: Partial<OrdinalLevelDef>) {
    onChangeLevels(levels.map((level, i) => (i === index ? { ...level, ...patch } : level)))
  }

  function removeLevel(index: number) {
    const removed = levels[index]
    onChangeLevels(levels.filter((_, i) => i !== index))
    if (removed) {
      const next = { ...levelDetails }
      delete next[removed.key]
      onChangeDetails(next)
    }
  }

  function addLevel() {
    const nextIndex = (levels[levels.length - 1]?.index ?? 0) + 1
    const key = `level_${nextIndex}`
    onChangeLevels([...levels, { key, label: `Level ${nextIndex}`, index: nextIndex }])
  }

  function applyStandard() {
    onChangeLevels(STANDARD_ORDINAL_LEVELS.map((l) => ({ ...l })))
  }

  function updateDetail(key: string, patch: Partial<LevelDetail>) {
    const prev = levelDetails[key] ?? {}
    onChangeDetails({ ...levelDetails, [key]: { ...prev, ...patch } })
  }

  return (
    <div className={hideToolbar ? 'space-y-3' : 'space-y-3 rounded-md border bg-muted/20 p-3'}>
      {!hideToolbar ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label className="text-sm font-medium">Performance levels</Label>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={applyStandard}>
              5-level standard
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={addLevel}>
              <Plus className="h-3.5 w-3.5 mr-1" aria-hidden />
              Add level
            </Button>
          </div>
        </div>
      ) : null}
      <div className="space-y-3">
        {levels.map((level, index) => {
          const detail = levelDetails[level.key] ?? {}
          return (
            <div key={`${level.key}-${index}`} className="rounded-md border bg-background p-3 space-y-2">
              <div className="flex flex-wrap items-end gap-2">
                <div className="space-y-1 flex-1 min-w-[8rem]">
                  <Label className="text-xs">Label</Label>
                  <Input
                    value={level.label}
                    onChange={(e) => updateLevel(index, { label: e.target.value })}
                  />
                </div>
                <div className="space-y-1 w-20">
                  <Label className="text-xs">Index</Label>
                  <Input
                    type="number"
                    min={1}
                    value={level.index}
                    onChange={(e) => updateLevel(index, { index: Number(e.target.value) })}
                  />
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeLevel(index)}>
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">What “{level.label || 'this level'}” looks like</Label>
                <Textarea
                  rows={2}
                  value={(detail.indicators ?? []).join('\n')}
                  onChange={(e) =>
                    updateDetail(level.key, {
                      indicators: e.target.value
                        .split('\n')
                        .map((line) => line.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="One observable indicator per line"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Optional prompts</Label>
                <Textarea
                  rows={1}
                  value={(detail.prompts ?? []).join('\n')}
                  onChange={(e) =>
                    updateDetail(level.key, {
                      prompts: e.target.value
                        .split('\n')
                        .map((line) => line.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="Interview or assessment prompts (optional)"
                />
              </div>
              <p className="text-xs text-muted-foreground font-mono">key: {level.key}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}
